import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import {
  IconButton,
  MenuItem,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useScheduleCandidate } from '../../hooks/useCandidate';
import CcEditor from './CcEditor';
import {
  GET_MANAGERS,
  GET_HR_CONTACTS,
  PREVIEW_INTERVIEW_EMAIL,
  SEND_INTERVIEW_EMAIL,
} from '../../api';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// ---- Design tokens (shared with the recruitment pages) ----
const ACCENT = '#2f6bff';
const GRADIENT_BTN = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '12px',
  px: 3,
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
// One outlined-field look reused by every text field, select and picker so the
// whole form aligns and shares hover/focus states.
const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&:hover .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
    borderColor: '#c7d2fe',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: ACCENT,
    borderWidth: '1.5px',
  },
};
// The app's fixed admin header sits at z-index 1301, above MUI's default modal
// (1300) — so the dialog is lifted to modal+2 or its title hides behind the
// header (HRP-FE-19). But that also lifts the dialog above the select menus and
// date/time poppers (which portal to <body> at 1300), so those must be raised
// higher still (modal+3) or they'd render behind the dialog.
const DIALOG_ZINDEX = (theme) => theme.zIndex.modal + 2;
const OVERLAY_ZINDEX = (theme) => theme.zIndex.modal + 3;
// Keeps a select's dropdown menu above the elevated dialog.
const SELECT_MENU_PROPS = { MenuProps: { sx: { zIndex: OVERLAY_ZINDEX } } };
// Shared textField + popper config for the date / time pickers.
const PICKER_SLOTS = {
  textField: { fullWidth: true, size: 'small', sx: FIELD_SX },
  popper: { sx: { zIndex: OVERLAY_ZINDEX } },
};
// Interview-time dropdowns: 12-hour hours ('01'..'12') and 5-minute steps ('00'..'55').
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

export default function SheduleBox({
  open,
  close,
  applicantData,
  selectedDate,
}) {
  const navigate = useNavigate();
  const handleClose = () => {
    close();
  };
  const { mutate: addCandidate } = useScheduleCandidate();
  // Step 1: Initialize state
  const [tm, setTm] = useState('');
  const [interviewemail, setInterviewemail] = useState('');
  const [, setInterviewname] = useState('');
  const [hrCcEmail, setHrCcEmail] = useState('');
  const [managers, setManagers] = useState([]);
  const [hrContacts, setHrContacts] = useState([]);
  const [selectedDateValue, setSelectedDateValue] = useState(null); // dayjs for the picker
  const [selectedTimeValue, setSelectedTimeValue] = useState(null); // dayjs for the picker

  // Interview time is chosen via three dropdowns (Hour / Minute / AM-PM). They
  // feed the single dayjs source of truth (selectedTimeValue) through
  // handleTimeChange, so the submit logic is unchanged. A time is only formed
  // once all three parts are picked; otherwise it stays null (submit falls back
  // to start-of-day, same as before).
  const [hourSel, setHourSel] = useState('');
  const [minuteSel, setMinuteSel] = useState('');
  const [meridiemSel, setMeridiemSel] = useState('');

  const applyTimeParts = (h, m, mer) => {
    if (h && m !== '' && mer) {
      let hour24 = parseInt(h, 10) % 12; // 12 -> 0
      if (mer === 'PM') hour24 += 12;
      handleTimeChange(dayjs().hour(hour24).minute(parseInt(m, 10)).second(0));
    } else {
      handleTimeChange(null);
    }
  };

  // Email preview-before-send (opens after the interview is scheduled)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null); // {subject,to,cc,html,text} | {error:true}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [ccList, setCcList] = useState([]); // editable Cc for the interview email
  const [scheduledName, setScheduledName] = useState('');
  const [scheduledId, setScheduledId] = useState(null);

  // After the candidate is saved, send (To: candidate, Cc: interviewer + HR)
  // only once HR has reviewed the generated email in the preview dialog.
  const handleSendInterviewEmail = async () => {
    setSending(true);
    try {
      await SEND_INTERVIEW_EMAIL({
        id: scheduledId,
        name: scheduledName,
        subject: previewData?.subject,
        html: previewData?.html,
        text: previewData?.text,
        cc: ccList,
      });
      alert('Interview email sent to the candidate (Cc: interviewer & HR).');
      setPreviewOpen(false);
      navigate('/scheduledcandidates');
    } catch (err) {
      console.error('Failed to send interview email', err);
      alert('Could not send the interview email. The interview is still scheduled.');
    } finally {
      setSending(false);
    }
  };

  // Close the preview without emailing — the interview stays scheduled.
  // Ignored while a send is in flight so the backdrop/Escape can't navigate
  // away mid-request.
  const handleSkipEmail = () => {
    if (sending) return;
    setPreviewOpen(false);
    navigate('/scheduledcandidates');
  };

  // Load reporting managers (interviewers) and HR contacts for the dropdowns
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [mgrRes, hrRes] = await Promise.allSettled([
          GET_MANAGERS(),
          GET_HR_CONTACTS(),
        ]);
        if (!active) return;
        if (mgrRes.status === 'fulfilled') {
          const m = mgrRes.value;
          setManagers(Array.isArray(m) ? m : m?.results || m?.managers || []);
        }
        if (hrRes.status === 'fulfilled') {
          const h = hrRes.value;
          setHrContacts(
            Array.isArray(h) ? h : h?.results || h?.hr_contacts || h?.data || []
          );
        }
      } catch (err) {
        console.error('Failed to load managers / HR contacts', err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    phone_number: '',
    email_address: '',
    interview_mode: 'virtual',
    interview_venue: '',
    interviewer_name: '',
    manual_interviewer_name: '',
    manual_interviewer_email: '',
    resume: '',
    portfolioLink: '',
    manual_meeting_link: '',
    hr_cc_name: '',
    hr_cc_email: '',
    manual_hr_cc_name: '',
    manual_hr_cc_email: '',
    additional_attachment: null,
  });

  // Handle time change and update state (keep the dayjs value + the HH:mm:ss string)
  const handleTimeChange = (newTime) => {
    setSelectedTimeValue(newTime);
    setTm(newTime ? newTime.format('HH:mm:ss') : '');
  };
  // Step 2: Prefill form data with applicantData when modal opens
  useEffect(() => {
    if (applicantData) {
      setFormData({
        name: applicantData.name,
        role: applicantData.role,
        phone_number: applicantData.phoneNumber,
        email_address: applicantData.email,

        resume: applicantData.resumeFile,
        portfolioLink: applicantData.portfolioLink,
      });
    }
  }, [applicantData]);

  // Reset to a blank form when opened for a manual add (no prefilled applicant)
  useEffect(() => {
    if (open && !applicantData) {
      setFormData({
        name: '',
        role: '',
        phone_number: '',
        email_address: '',
        interview_mode: 'virtual',
        interview_venue: '',
        interviewer_name: '',
        manual_interviewer_name: '',
        manual_interviewer_email: '',
        resume: '',
        portfolioLink: '',
        manual_meeting_link: '',
        hr_cc_name: '',
        hr_cc_email: '',
        manual_hr_cc_name: '',
        manual_hr_cc_email: '',
        additional_attachment: null,
      });
      setTm('');
      setInterviewemail('');
      setInterviewname('');
      setHrCcEmail('');
      setSelectedDateValue(null);
      setSelectedTimeValue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, applicantData]);

  // Clear the Hour / Minute / AM-PM dropdowns whenever the modal (re)opens — and
  // the underlying dayjs value + HH:mm:ss string with them, so a previously
  // chosen time can't be silently submitted for the next candidate (the
  // applicant-prefill open path doesn't otherwise reset these).
  useEffect(() => {
    if (open) {
      setHourSel('');
      setMinuteSel('');
      setMeridiemSel('');
      setSelectedTimeValue(null);
      setTm('');
    }
  }, [open]);

  // Step 3: Handle input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange = (newDate) => {
    setSelectedDateValue(newDate);
  };

  const handleInterviewEmail = (mgr) => {
    setInterviewname(mgr.name);
    setInterviewemail(mgr.email || '');
  };

  // Step 4: Handle form submission
  const handleSubmit = () => {
    // interview_date is a DateTimeField on the backend — combine the chosen date
    // and time into a full datetime string so the serializer accepts it.
    const interviewDateTime = selectedDateValue
      ? (selectedTimeValue
          ? selectedDateValue
              .hour(selectedTimeValue.hour())
              .minute(selectedTimeValue.minute())
              .second(0)
          : selectedDateValue.startOf('day')
        ).format('YYYY-MM-DDTHH:mm:ss')
      : '';

    const submissionData = {
      ...formData,
      interviewer_name:
        formData.interviewer_name === 'manual'
          ? formData.manual_interviewer_name
          : formData.interviewer_name,
      interviewemail:
        formData.interviewer_name === 'manual'
          ? formData.manual_interviewer_email
          : interviewemail,
      hr_cc_name:
        formData.hr_cc_name === 'manual'
          ? formData.manual_hr_cc_name
          : formData.hr_cc_name,
      hr_cc_email:
        formData.hr_cc_name === 'manual'
          ? formData.manual_hr_cc_email
          : hrCcEmail,
      interview_date: interviewDateTime,
      interview_time: tm,
    };
    if (!submissionData.interviewer_name || !submissionData.interview_date || !submissionData.interview_time) {
      alert("Please fill in all required fields (Interviewer, Interview Date, Interview Time)"); // Alert if data is missing
      return;
    }

    addCandidate(submissionData, {
      onSuccess: async (data) => {
        // Interview saved. Close the form, then open the email preview so HR can
        // review (To: candidate, Cc: interviewer + HR) before it's sent. Pin the
        // created record's id so preview/send target the exact candidate.
        const candidateName = submissionData.name;
        const candidateId = data?.id ?? null;
        setScheduledName(candidateName);
        setScheduledId(candidateId);
        handleClose();
        setPreviewData(null);
        setCcList([]);
        setPreviewLoading(true);
        setPreviewOpen(true);
        try {
          const previewResult = await PREVIEW_INTERVIEW_EMAIL({ id: candidateId, name: candidateName });
          setPreviewData(previewResult);
          setCcList(previewResult?.cc || []);
        } catch (err) {
          console.error('Failed to build interview email preview', err);
          setPreviewData({ error: true });
        } finally {
          setPreviewLoading(false);
        }
      },
      onError: (error) => {
        console.error('Error adding candidate:', error);
        const url = (error?.config?.baseURL || '') + (error?.config?.url || '');
        const status = error?.response?.status || 'no response (network / CORS)';
        const detail = error?.response?.data
          ? JSON.stringify(error.response.data)
          : error?.message || '';
        alert(`Could not schedule interview.\nURL: ${url}\nStatus: ${status}\n${detail}`);
      },
    });
  };

  const isManualInterviewer = formData.interviewer_name === 'manual';
  const isManualHr = formData.hr_cc_name === 'manual';
  const isFaceToFace = formData.interview_mode === 'face_to_face';

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{ zIndex: DIALOG_ZINDEX }}
        PaperProps={{ sx: { borderRadius: '22px' } }}
        aria-labelledby="schedule-interview-title"
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2.5, sm: 3.5 },
            pt: 3,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: '#eef3ff',
                color: ACCENT,
                flexShrink: 0,
              }}
            >
              <CalendarMonthIcon />
            </Box>
            <Box>
              <Typography
                id="schedule-interview-title"
                sx={{ fontWeight: 800, fontSize: '22px', color: '#111827', lineHeight: 1.2 }}
              >
                Schedule Interview
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>
                Add a candidate and set up their first interview.
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: '#9ca3af' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
          {/* ---- Candidate details ---- */}
          <SectionTitle>Candidate Details</SectionTitle>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Label>Candidate Name</Label>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter candidate's full name"
                fullWidth
                size="small"
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Role / Position</Label>
              <TextField
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                placeholder="Enter role / position"
                fullWidth
                size="small"
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Candidate Phone Number</Label>
              <TextField
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder="Enter candidate's phone number"
                fullWidth
                size="small"
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Candidate Email</Label>
              <TextField
                name="email_address"
                value={formData.email_address}
                onChange={handleInputChange}
                placeholder="Enter candidate's email"
                fullWidth
                size="small"
                sx={FIELD_SX}
              />
            </Grid>
          </Grid>

          {/* ---- Interview details ---- */}
          <SectionTitle sx={{ mt: 3.5 }}>Interview Details</SectionTitle>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Label>Interviewer Name</Label>
              <TextField
                select
                name="interviewer_name"
                value={formData.interviewer_name || ''}
                onChange={(e) =>
                  setFormData({ ...formData, interviewer_name: e.target.value })
                }
                fullWidth
                size="small"
                sx={FIELD_SX}
                SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
              >
                <MenuItem value="">Select Interviewer</MenuItem>
                {managers.map((manage) => (
                  <MenuItem
                    key={manage.id}
                    value={manage.name}
                    onClick={() => handleInterviewEmail(manage)}
                  >
                    {manage.name}
                  </MenuItem>
                ))}
                <MenuItem value="manual">Enter Manually</MenuItem>
              </TextField>
            </Grid>

            {!isManualInterviewer && (
              <Grid item xs={12} sm={6}>
                <Label>Interview Email</Label>
                <TextField
                  name="interview_email"
                  value={interviewemail}
                  disabled
                  placeholder="Auto-filled from interviewer"
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                />
              </Grid>
            )}

            {isManualInterviewer && (
              <>
                <Grid item xs={12} sm={6}>
                  <Label>Interviewer Name (manual)</Label>
                  <TextField
                    name="manual_interviewer_name"
                    value={formData.manual_interviewer_name || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, manual_interviewer_name: e.target.value })
                    }
                    placeholder="Enter interviewer name"
                    fullWidth
                    size="small"
                    sx={FIELD_SX}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label>Interviewer Email (manual)</Label>
                  <TextField
                    name="manual_interviewer_email"
                    value={formData.manual_interviewer_email || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, manual_interviewer_email: e.target.value })
                    }
                    placeholder="Enter interviewer email"
                    fullWidth
                    size="small"
                    sx={FIELD_SX}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <Label>Interview Date</Label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={selectedDateValue}
                  onChange={handleDateChange}
                  slotProps={PICKER_SLOTS}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>Interview Time</Label>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  select
                  value={hourSel}
                  onChange={(e) => {
                    setHourSel(e.target.value);
                    applyTimeParts(e.target.value, minuteSel, meridiemSel);
                  }}
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                  SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
                >
                  <MenuItem value="" disabled>HH</MenuItem>
                  {HOUR_OPTIONS.map((h) => (
                    <MenuItem key={h} value={h}>{h}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  value={minuteSel}
                  onChange={(e) => {
                    setMinuteSel(e.target.value);
                    applyTimeParts(hourSel, e.target.value, meridiemSel);
                  }}
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                  SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
                >
                  <MenuItem value="" disabled>MM</MenuItem>
                  {MINUTE_OPTIONS.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  value={meridiemSel}
                  onChange={(e) => {
                    setMeridiemSel(e.target.value);
                    applyTimeParts(hourSel, minuteSel, e.target.value);
                  }}
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                  SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
                >
                  <MenuItem value="" disabled>AM/PM</MenuItem>
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </TextField>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Label>Interview Mode</Label>
              <TextField
                select
                name="interview_mode"
                value={formData.interview_mode || 'virtual'}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    interview_mode: value,
                    // Face-to-face has no meeting link — clear any link entered
                    // earlier so a stale link is never submitted.
                    ...(value === 'face_to_face' ? { manual_meeting_link: '' } : {}),
                  }));
                }}
                fullWidth
                size="small"
                sx={FIELD_SX}
                SelectProps={SELECT_MENU_PROPS}
              >
                <MenuItem value="virtual">Virtual</MenuItem>
                <MenuItem value="face_to_face">Face-to-Face</MenuItem>
              </TextField>
            </Grid>

            {/* Meeting link for virtual; venue for face-to-face */}
            {isFaceToFace ? (
              <Grid item xs={12} sm={6}>
                <Label>Interview Venue</Label>
                <TextField
                  name="interview_venue"
                  value={formData.interview_venue || ''}
                  onChange={handleInputChange}
                  placeholder="Office address / room"
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <Label>Meeting Link (Zoom, Meet)</Label>
                <TextField
                  name="manual_meeting_link"
                  value={formData.manual_meeting_link}
                  onChange={handleInputChange}
                  placeholder="Paste Zoom / Google Meet link"
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                />
              </Grid>
            )}
          </Grid>

          {/* ---- HR notification ---- */}
          <SectionTitle sx={{ mt: 3.5 }}>HR Notification (CC)</SectionTitle>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Label>HR CC Name</Label>
              <TextField
                select
                name="hr_cc_name"
                value={formData.hr_cc_name || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, hr_cc_name: value });
                  if (value === '' || value === 'manual') setHrCcEmail('');
                }}
                fullWidth
                size="small"
                sx={FIELD_SX}
                SelectProps={{ displayEmpty: true, ...SELECT_MENU_PROPS }}
              >
                <MenuItem value="">Select HR</MenuItem>
                {hrContacts.map((contact) => (
                  <MenuItem
                    key={contact.id}
                    value={contact.name}
                    onClick={() => setHrCcEmail(contact.email || '')}
                  >
                    {contact.name}
                  </MenuItem>
                ))}
                <MenuItem value="manual">Enter Manually</MenuItem>
              </TextField>
            </Grid>

            {!isManualHr && (
              <Grid item xs={12} sm={6}>
                <Label>HR CC Email</Label>
                <TextField
                  name="hr_cc_email"
                  value={hrCcEmail}
                  disabled
                  placeholder="Auto-filled from HR contact"
                  fullWidth
                  size="small"
                  sx={FIELD_SX}
                />
              </Grid>
            )}

            {isManualHr && (
              <>
                <Grid item xs={12} sm={6}>
                  <Label>HR CC Name (manual)</Label>
                  <TextField
                    name="manual_hr_cc_name"
                    value={formData.manual_hr_cc_name || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, manual_hr_cc_name: e.target.value })
                    }
                    placeholder="Enter HR name"
                    fullWidth
                    size="small"
                    sx={FIELD_SX}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label>HR CC Email (manual)</Label>
                  <TextField
                    name="manual_hr_cc_email"
                    value={formData.manual_hr_cc_email || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, manual_hr_cc_email: e.target.value })
                    }
                    placeholder="Enter HR email"
                    fullWidth
                    size="small"
                    sx={FIELD_SX}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* ---- Attachment ---- */}
          <SectionTitle sx={{ mt: 3.5 }}>Attachment</SectionTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              border: '1px dashed #d1d5db',
              borderRadius: '12px',
              px: 2,
              py: 1.75,
              bgcolor: '#fafbff',
            }}
          >
            <Button
              component="label"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                borderColor: '#e5e7eb',
                color: '#374151',
              }}
            >
              {formData.additional_attachment ? 'Change file' : 'Upload resume (PDF/DOCX)'}
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={(e) =>
                  setFormData({ ...formData, additional_attachment: e.target.files[0] })
                }
              />
            </Button>
            <Typography sx={{ fontSize: '13px', color: formData.additional_attachment ? '#111827' : '#9ca3af' }}>
              {formData.additional_attachment
                ? formData.additional_attachment.name
                : 'No file selected'}
            </Typography>
          </Box>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2 }}>
          <Button
            onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={<EventAvailableIcon />}
            onClick={handleSubmit}
            sx={GRADIENT_BTN}
          >
            Schedule Interview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email preview — shown after scheduling; HR sends only after reviewing */}
      <Dialog
        open={previewOpen}
        onClose={handleSkipEmail}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: DIALOG_ZINDEX }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Interview Email Preview</DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : previewData?.error ? (
            <Alert severity="warning">
              The interview was scheduled, but the email preview couldn't be loaded.
              You can still proceed — the interview remains scheduled.
            </Alert>
          ) : previewData ? (
            <>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>To:</strong> {(previewData.to || []).join(', ') || '—'}
              </Typography>
              <Box sx={{ mb: 0.75 }}>
                <CcEditor value={ccList} onChange={setCcList} accent={ACCENT} />
              </Box>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>Subject:</strong> {previewData.subject}
              </Typography>
              {(!previewData.to || previewData.to.length === 0) && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  This candidate has no email address — sending will fail.
                </Alert>
              )}
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                <iframe
                  title="interview-email-preview"
                  srcDoc={previewData.html || ''}
                  style={{ width: '100%', height: '460px', border: 'none' }}
                />
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleSkipEmail} color="inherit" disabled={sending}>
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={handleSendInterviewEmail}
            disabled={
              sending ||
              previewLoading ||
              !previewData ||
              previewData.error ||
              !(previewData.to && previewData.to.length)
            }
          >
            {sending ? 'Sending…' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Small uppercase section heading that groups related fields.
const SectionTitle = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: '12px',
      fontWeight: 800,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#9ca3af',
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

// One label style for every field, rendered above the input.
const Label = (props) => (
  <InputLabel
    {...props}
    sx={{
      mb: '6px',
      display: 'block',
      fontSize: '13px',
      fontWeight: 600,
      color: '#374151',
      ...props.sx,
    }}
  />
);
